// Configuración → Plan de una empresa con facturación por marca: una fila por marca
// (ADR-2026-10-28-plan-por-marca-en-configuracion). La lógica vive en
// `components/facturacionPorMarca/planesPorMarca.ts`; acá sólo se arma la pantalla.
// También la usa "Facturación de tu plan" (`FacturacionSuscripcion`) en lugar de la suscripción
// de la empresa, sin su título: lo pone la card del portal
// (ADR-2026-10-29-facturacion-con-planes-por-marca).
// "Cambiar tarjeta" de cada marca y el detalle de su cobro rechazado:
// ADR-2026-10-29-cobro-rechazado-por-marca. La recarga automática de cada marca con plan:
// ADR-2026-10-29-recarga-automatica-de-marca.

import { useCallback, useEffect, useState } from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { cancelarPlanDeMarca, estadoDeMarcas, type EstadoDeMarca } from '../../lib/paymentServer';
import {
  abrirCambioDeTarjetaDeMarca,
  ErrorAlCambiarTarjetaDeMarca,
  leerCobroDeBilleteras,
} from '../../lib/cobroRechazado';
import { cuerpoDeCobro } from '../../lib/textosDeCobro';
import { tonoDeCobro } from '../../components/cobros/AvisoDeCobro';
import ElegirPlanDeMarca from '../../components/facturacionPorMarca/ElegirPlanDeMarca';
import RecargaAutomatica from '../../components/facturacionPorMarca/RecargaAutomatica';
import { useUserGroup } from '../../hooks/useUserGroup';
import {
  filasDePlanPorMarca,
  mensajeDeErrorAlCancelarMarca,
  type FilaDePlanDeMarca,
} from '../../components/facturacionPorMarca/planesPorMarca';

const fechaLarga = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });

interface Props {
  idCompany: string;
  setError: (e: string | null) => void;
  setSuccess: (s: string | null) => void;
  /** Título "Suscripción" (o "Suscripciones", con más de una marca con plan) y bajada propios. `false` cuando lo envuelve una card con su título. */
  conEncabezado?: boolean;
}

export default function PlanPorMarca({ idCompany, setError, setSuccess, conEncabezado = true }: Props) {
  const [filas, setFilas] = useState<FilaDePlanDeMarca[] | null>(null);
  const [elegir, setElegir] = useState<{ brandId: string; abierto: boolean } | null>(null);
  const [confirmar, setConfirmar] = useState<FilaDePlanDeMarca | null>(null);
  const [cancelando, setCancelando] = useState(false);
  // La marca cuya página de Rebill se está abriendo (el botón gira hasta que el navegador se va).
  const [abriendoTarjeta, setAbriendoTarjeta] = useState<string | null>(null);
  // Marcas cuyo cambio de tarjeta contestó `sin_suscripcion`: el botón queda apagado.
  const [sinSuscripcion, setSinSuscripcion] = useState<ReadonlySet<string>>(() => new Set());
  const { isDevTester } = useUserGroup();

  const cargar = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');
      const [{ data: marcas, error: eM }, r, billeteras] = await Promise.all([
        supabase
          .from('brand_profiles')
          .select('id, name, is_default')
          .eq('id_company', idCompany)
          .eq('is_draft', false)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: true }),
        estadoDeMarcas(user.id),
        // El detalle del rechazo (motivo, tarjeta, reintento, morosidad) por RLS; tolerante.
        leerCobroDeBilleteras(idCompany),
      ]);
      if (eM) throw eM;
      if (!r.ok) throw new Error('No pudimos leer los planes de tus marcas. Probá de nuevo.');
      setFilas(
        filasDePlanPorMarca(
          (marcas ?? []) as { id: string; name: string; is_default: boolean }[],
          r.marcas as EstadoDeMarca[],
          new Map(billeteras.map((b) => [b.brand_id, b])),
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar los planes');
      setFilas((f) => f ?? []);
    }
  }, [idCompany, setError]);

  useEffect(() => {
    void cargar();
    const alCambiarCreditos = () => void cargar();
    window.addEventListener('atlas:credits-updated', alCambiarCreditos);
    return () => window.removeEventListener('atlas:credits-updated', alCambiarCreditos);
  }, [cargar]);

  const cancelar = async () => {
    if (!confirmar) return;
    setCancelando(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');
      const r = await cancelarPlanDeMarca({ userId: user.id, brandId: confirmar.brandId });
      if (r.ok) {
        setSuccess(`El plan de ${confirmar.nombre} se cancela el ${fechaLarga(r.fecha_cancelacion_programada)}. Hasta ahí seguís usando los créditos.`);
      } else {
        setError(mensajeDeErrorAlCancelarMarca(r.error));
      }
      setConfirmar(null);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos cancelar el plan.');
    } finally {
      setCancelando(false);
    }
  };

  const cambiarTarjeta = async (f: FilaDePlanDeMarca) => {
    if (abriendoTarjeta) return;
    setError(null);
    setAbriendoTarjeta(f.brandId);
    try {
      // Navega a Rebill en la misma pestaña: si sale bien, el botón queda girando hasta irse.
      await abrirCambioDeTarjetaDeMarca(f.brandId);
    } catch (e) {
      setAbriendoTarjeta(null);
      if (e instanceof ErrorAlCambiarTarjetaDeMarca && e.codigo === 'sin_suscripcion') {
        setSinSuscripcion((s) => new Set(s).add(f.brandId));
      }
      setError(e instanceof Error ? e.message : 'No pudimos abrir el cambio de tarjeta. Probá de nuevo.');
    }
  };

  if (!filas) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div>
      {conEncabezado && (
        <>
          {/* En plural cuando hay más de una marca con plan (pedido de Matías, 2026-10-29). */}
          <h2 className="text-[17px] font-semibold text-brand-black/90 mb-1">
            {filas.filter((f) => f.tienePlan).length > 1 ? 'Suscripciones' : 'Suscripción'}
          </h2>
          <p className="text-[13px] text-neutral-500 mb-4">Cada marca tiene su propio plan y sus propios créditos.</p>
        </>
      )}
      <div className="flex flex-col gap-3">
        {filas.map((f) => {
          // Con un cobro pendiente la fila se tiñe como el aviso de la home y lo primero es la tarjeta.
          const caja = f.cobro
            ? tonoDeCobro(f.cobro).caja
            : f.cobroRechazado
              ? 'bg-amber-50 border-amber-200'
              : 'border-neutral-200 bg-surface-card';
          const tarjetaApagada = sinSuscripcion.has(f.brandId) || abriendoTarjeta !== null;
          return (
            <div key={f.brandId} className={`p-4 border rounded-lg ${caja}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-brand-black/90 truncate">{f.nombre}</p>
                  <p className="text-[14px] text-neutral-500">
                    {f.plan ? <span className="font-medium text-brand-primary">{f.plan}</span> : 'Sin plan'}
                    {' · '}
                    {f.disponibles.toLocaleString('es-AR')} {f.disponibles === 1 ? 'crédito' : 'créditos'}
                  </p>
                  {f.fecha && (
                    <p className="text-[13px] text-neutral-500 mt-0.5">
                      {f.fechaEs === 'baja' ? `Se cancela el ${fechaLarga(f.fecha)}` : `Próxima renovación: ${fechaLarga(f.fecha)}`}
                    </p>
                  )}
                  {f.cobro ? (
                    <p className="text-[13px] leading-snug text-brand-black/70 mt-1 max-w-prose">
                      {cuerpoDeCobro({ ...f.cobro, marca: { brandId: f.brandId, nombre: f.nombre, esDueno: true, otras: [] } })}
                    </p>
                  ) : (
                    f.cobroRechazado && (
                      <p className="text-[13px] text-red-600 mt-1">
                        No pudimos cobrar la renovación de esta marca. Cambiá su tarjeta para pagarla.
                      </p>
                    )
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {f.puedeCambiarTarjeta && (
                    <button
                      type="button"
                      onClick={() => void cambiarTarjeta(f)}
                      disabled={tarjetaApagada}
                      title={sinSuscripcion.has(f.brandId) ? 'Esta marca no tiene una suscripción con tarjeta para cambiar.' : undefined}
                      className={`inline-flex items-center justify-center gap-1.5 py-2 px-4 text-[14px] font-medium rounded-md transition-[background-color,transform] duration-150 ease-out enabled:active:scale-[0.96] disabled:opacity-60 ${
                        f.cobroPendiente
                          ? 'bg-brand-primary enabled:hover:bg-brand-primary-hover text-white shadow-sm'
                          : 'border border-neutral-200 bg-surface-card enabled:hover:bg-neutral-50 text-brand-black/90'
                      }`}
                    >
                      {abriendoTarjeta === f.brandId ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      {/* Con un cobro pendiente Rebill lo cobra apenas se cambia la tarjeta: el botón lo dice. */}
                      {f.cobroPendiente ? 'Cambiar tarjeta y pagar' : 'Cambiar tarjeta'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setElegir({ brandId: f.brandId, abierto: true })}
                    className={`py-2 px-4 text-[14px] font-medium rounded-md transition-colors active:scale-[0.96] ${
                      f.cobroPendiente && f.puedeCambiarTarjeta
                        ? 'border border-neutral-200 bg-surface-card hover:bg-neutral-50 text-brand-black/90'
                        : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                    }`}
                  >
                    {f.tienePlan ? 'Cambiar plan' : 'Elegir plan'}
                  </button>
                  {f.puedeCancelar && (
                    <button
                      type="button"
                      onClick={() => setConfirmar(f)}
                      className="py-2 px-4 border border-neutral-200 bg-surface-card hover:bg-neutral-50 text-brand-black/90 text-[14px] font-medium rounded-md transition-colors active:scale-[0.96]"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
              {f.recarga && (
                <RecargaAutomatica
                  variante="linea"
                  brandId={f.brandId}
                  recarga={f.recarga}
                  devTester={isDevTester === true}
                  onCambio={(recarga) =>
                    setFilas((fs) => fs?.map((x) => (x.brandId === f.brandId ? { ...x, recarga } : x)) ?? fs)
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      {confirmar && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4 animate-fade-in"
          onClick={() => !cancelando && setConfirmar(null)}
        >
          <div className="bg-surface-card rounded-lg shadow-lg max-w-sm w-full p-5 animate-modal-appear" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1">Cancelar el plan de {confirmar.nombre}</h3>
            <p className="text-[13px] text-neutral-500 mb-4">
              {confirmar.fecha && confirmar.fechaEs === 'renovacion'
                ? `La marca sigue usando sus créditos hasta el ${fechaLarga(confirmar.fecha)}. Después no se renueva.`
                : 'La marca sigue usando sus créditos hasta el fin del período. Después no se renueva.'}
            </p>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setConfirmar(null)}
                disabled={cancelando}
                className="py-2.5 px-5 border border-neutral-200 rounded-md bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-50 transition-colors"
              >
                Mantener plan
              </button>
              <button
                type="button"
                onClick={cancelar}
                disabled={cancelando}
                className="py-2.5 px-5 bg-red-600 hover:bg-red-700 text-white text-[15px] font-medium rounded-md transition-colors flex items-center justify-center gap-2"
              >
                {cancelando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancelar plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {elegir && (
        <ElegirPlanDeMarca
          abierto={elegir.abierto}
          brandId={elegir.brandId}
          modo={filas.find((f) => f.brandId === elegir.brandId)?.tienePlan ? 'cambiar' : 'contratar'}
          onListo={() => {
            setElegir((e) => (e ? { ...e, abierto: false } : e));
            void cargar();
          }}
        />
      )}
    </div>
  );
}
